import type { AnyAction } from 'redux';
import type { ThunkAction } from 'redux-thunk';

import { ENDPOINTS } from '../../../shared/organizationPackages';
import { nuc } from '../../utils';
import * as API from '../api';
import { toAsyncActionTypes } from '../utils';
import type {
  AnyFilter,
  FetchPackageDetailsResponse,
  FetchPackageMetadataResponse,
  FetchPackagesQuery,
  FetchPackagesResponse,
  FetchProjectsResponse,
  FetchVersionDetailsResponse,
  PackageDetails,
} from '../../../shared/organizationPackages';
import type { SortOption } from '../../../shared/organizationPackages/sortOptions';
import type { PackageInventoryQueryParams } from '../../helpers/packageInventory';
import type { ReduxAction, SyncReduxAction } from '../../types';
import type { ReduxStore } from '../store';

import {
  getFilters,
  getHydratePackagesStateQuery,
  getHydrateVersionDetailsStateQuery,
  getSelectedVuln,
} from './selectors';
import type { HydrateStateQuery } from './types';

//  Action types, divided into sync and async actions.
export const actionTypes = {
  sync: {
    HYDRATE_PACKAGES_QUERY_STATE: 'PACKAGE_INVENTORY__HYDRATE_PACKAGES_QUERY_STATE',
    HYDRATE_VERSION_DETAILS_QUERY_STATE: 'PACKAGE_INVENTORY__HYDRATE_VERSION_DETAILS_STATE',
    PRE_FETCH_LOADING: 'PACKAGE_INVENTORY__PRE_FETCH_LOADING',
    SET_PACKAGES_PAGE: 'PACKAGE_INVENTORY__SET_PACKAGES_PAGE',
    SET_PACKAGES_SORT: 'PACKAGE_INVENTORY__SET_PACKAGES_SORT',
    SET_PACKAGES_COUNT: 'PACKAGE_INVENTORY__SET_PACKAGES_COUNT',
    SET_VERSION_DETAILS_PAGE: 'PACKAGE_INVENTORY__SET_VERSION_DETAILS_PAGE',
    SET_VERSION_DETAILS_COUNT: 'PACKAGE_INVENTORY__SET_VERSION_DETAILS_COUNT',
    RESET_PACKAGES_PAGE: 'PACKAGE_INVENTORY__RESET_PACKAGES_PAGE',
    RESET_VERSION_DETAILS_PAGE: 'PACKAGE_INVENTORY__RESET_VERSION_DETAILS_PAGE',
    RESET_FILTERS: 'PACKAGE_INVENTORY__RESET_FILTERS',
    SET_FILTERS: 'PACKAGE_INVENTORY__SET_FILTERS',
    RESET_PACKAGE_VERSION_DETAILS: 'PACKAGE_INVENTORY__RESET_PACKAGE_VERSION_DETAILS',
    RESET_PACKAGE_DETAILS: 'PACKAGE_INVENTORY__RESET_PACKAGE_DETAILS',
    RESET_SELECTED_VULN: 'PACKAGE_INVENTORY__RESET_SELECTED_VULN',
    TOGGLE_VERSION_SELECTED: 'PACKAGE_INVENTORY__TOGGLE_VERSION_SELECTED',
    TOGGLE_SELECTED_ALL_VERSIONS: 'PACKAGE_INVENTORY__TOGGLE_SELECTED_ALL_VERSIONS',
  },
  async: {
    GET_PACKAGES: 'PACKAGE_INVENTORY__GET_PACKAGES',
    GET_PACKAGE_DETAILS: 'PACKAGE_INVENTORY__GET_PACKAGE_DETAILS',
    GET_PACKAGE_PROJECTS: 'PACKAGE_INVENTORY__GET_PACKAGE_PROJECTS',
    GET_PACKAGE_METADATA: 'PACKAGE_INVENTORY__GET_PACKAGE_METADATA',
    GET_PACKAGE_VERSION_METADATA: 'PACKAGE_INVENTORY__GET_PACKAGE_VERSION_METADATA',
    GET_PACKAGE_MANAGERS: 'PACKAGE_INVENTORY__GET_PACKAGE_MANAGERS',
    GET_PACKAGE_LOCATORS: 'PACKAGE_INVENTORY__GET_PACKAGE_LOCATORS',
    GET_PACKAGE_SUMMARY: 'PACKAGE_INVENTORY__GET_PACKAGE_SUMMARY',
    GET_CWES: 'PACKAGE_INVENTORY__GET_CWES',
    GET_PACKAGE_VULNERABILITIES: 'PACKAGE_INVENTORY__GET_PACKAGE_VULNERABILITIES',
    GET_SELECTED_VULN: 'PACKAGE_INVENTORY__GET_SELECTED_VULN',
    BLOCK_PACKAGE_VERSIONS: 'PACKAGE_INVENTORY__BLOCK_PACKAGE_VERSIONS',
  },
};

//  All of the async action types, but async-ified to have a PENDING, FULFILLED, ETC.
export const asyncActionTypes = Object.freeze(toAsyncActionTypes(actionTypes.async));

export const syncActionTypes = Object.freeze(actionTypes.sync);

//  Responsible for telling the query params to be updated in response to packages state updates.
export const hydratePackagesQueryStateAction =
  (query: HydrateStateQuery): SyncReduxAction<{ query: HydrateStateQuery }> => ({
    type: actionTypes.sync.HYDRATE_PACKAGES_QUERY_STATE,
    query,
  });

//  Responsible for telling the query params to be updated in response to version details state updates.
export const hydrateVersionDetailsQueryStateAction =
  (query: HydrateStateQuery): SyncReduxAction<{ query: HydrateStateQuery }> => ({
    type: actionTypes.sync.HYDRATE_VERSION_DETAILS_QUERY_STATE,
    query,
  });

//  This tells the UI to show the loading animation immediately
export const preFetchLoading = () => ({
  type: actionTypes.sync.PRE_FETCH_LOADING,
});

/**
 * Action for fetching the list of packages.
 */
const fetchPackagesAction = (hydrateStateQuery: PackageInventoryQueryParams): ReduxAction<FetchPackagesResponse> => ({
  type: actionTypes.async.GET_PACKAGES,
  payload: async () => API.get(ENDPOINTS.FETCH_PACKAGES, true, hydrateStateQuery, {
    encodeQuery: true,
  }),
});

/**
 * Thunk action for fetching packages.
 * read the state and pass along filters to the API.
 */
export const fetchPackages = (): ThunkAction<void, ReduxStore, never, AnyAction> => (dispatch, getState) => {
  const hydrateStateQuery = getHydratePackagesStateQuery(getState());
  return dispatch(fetchPackagesAction(hydrateStateQuery));
};

/**
 * Action for fetching package details.
 *
 * @param {string} packageLocator - A given package locator
 */
const fetchPackageDetailsAction = (
  packageLocator: string,
  query: PackageInventoryQueryParams,
): ReduxAction<FetchPackageDetailsResponse> => ({
  type: actionTypes.async.GET_PACKAGE_DETAILS,
  payload: async () => API.get(`${ENDPOINTS.FETCH_PACKAGES}/${nuc(packageLocator)}/versions`, true, query, {
    encodeQuery: true,
  }),
});

export const fetchPackageDetails =
  (packageLocator: string): ThunkAction<void, ReduxStore, never, AnyAction> =>
    (dispatch, getState) => {
      const hydrateStateQuery = getHydratePackagesStateQuery(getState());
      return dispatch(fetchPackageDetailsAction(packageLocator, hydrateStateQuery));
    };

const fetchPackageMetadataAction = (
  packageLocator: string,
  query: PackageInventoryQueryParams,
): ReduxAction<FetchPackageMetadataResponse> => ({
  type: actionTypes.async.GET_PACKAGE_METADATA,
  payload: async () => API.get(`${ENDPOINTS.FETCH_PACKAGES}/${nuc(packageLocator)}`, true, query, {
    encodeQuery: true,
  }),
});

export const fetchPackageMetadata =
  (packageLocator: string): ThunkAction<void, ReduxStore, never, AnyAction> =>
    (dispatch, getState) => {
      const hydrateStateQuery = getHydratePackagesStateQuery(getState());
      return dispatch(fetchPackageMetadataAction(packageLocator, hydrateStateQuery));
    };

//  Action for updating the packages page.
export const setPackagesPage = (page: number): SyncReduxAction<{ page: number }> => ({
  type: actionTypes.sync.SET_PACKAGES_PAGE,
  page,
});

// Action for updating the sort.
export const setPackagesSort = (sort: SortOption): SyncReduxAction<{ sort: SortOption }> => ({
  type: actionTypes.sync.SET_PACKAGES_SORT,
  sort,
});

//  Sets the packages count (ie: "page size").
export const setPackagesCount = (count: number): SyncReduxAction<{ count: number }> => ({
  type: actionTypes.sync.SET_PACKAGES_COUNT,
  count,
});

//  Action for updating the version details page.
export const setVersionDetailsPage = (page: number): SyncReduxAction<{ page: number }> => ({
  type: actionTypes.sync.SET_VERSION_DETAILS_PAGE,
  page,
});

//  Sets the version details count (ie: "page size").
export const setVersionDetailsCount = (count: number): SyncReduxAction<{ count: number }> => ({
  type: actionTypes.sync.SET_VERSION_DETAILS_COUNT,
  count,
});

//  Resets the current packages page.
export const resetPackagesPage = (): AnyAction => ({
  type: actionTypes.sync.RESET_PACKAGES_PAGE,
});

//  Resets the current version details page.
export const resetVersionDetailsPage = (): AnyAction => ({
  type: actionTypes.sync.RESET_VERSION_DETAILS_PAGE,
});

//  Resets any of the filters currently present, including search.
export const resetFilters = (): AnyAction => ({
  type: actionTypes.sync.RESET_FILTERS,
});

//  Action for updating any of the filter fields related to packages.
//  If a field is present it will be updated, otherwise it will be skipped.
export const setFiltersAction = (filters: AnyFilter): SyncReduxAction<{ filters: AnyFilter }> => ({
  type: actionTypes.sync.SET_FILTERS,
  filters,
});

/**
 * Thunk which allows us to pass just a partial of the filters and have the
 * rest of the filters which have been set included in the action.
 */
export const setFilters = (filters: AnyFilter): ThunkAction<void, ReduxStore, never, AnyAction> =>
  (dispatch, getState) => {
    const stateFilters = getFilters(getState());
    return dispatch(setFiltersAction({
      ...stateFilters,
      ...filters,
    }));
  };

/**
 * Fetches the list of user projects associated with a given package + version.
 *
 * @param {string} packageLocator - Given package locator.
 * @param {string} version - Given package version.
 */
export const fetchPackageProjectsAction = (
  packageLocator: string,
  version: string,
  query: PackageInventoryQueryParams,
): ReduxAction<FetchProjectsResponse> => ({
  type: actionTypes.async.GET_PACKAGE_PROJECTS,
  payload: async () =>
    API.get(`${ENDPOINTS.FETCH_PACKAGES}/${nuc(packageLocator)}/versions/${nuc(version)}/projects`, true, query, {
      encodeQuery: true,
    }),
});

/**
 * Fetches the list of vulnerabilities associated with a given package + version.
 * @param packageLocator
 * @param version
 */
export const fetchPackageVulnerabilities = (packageLocator: string, version: string) => ({
  type: actionTypes.async.GET_PACKAGE_VULNERABILITIES,
  payload: async () => API.get(`${ENDPOINTS.FETCH_PACKAGES}/${nuc(packageLocator)}/versions/${nuc(version)}/vulnerabilities`, true),
});

export const fetchPackageProjects =
  (packageLocator: string, version: string): ThunkAction<void, ReduxStore, never, AnyAction> =>
    (dispatch, getState) => {
      const hydrateStateQuery = getHydrateVersionDetailsStateQuery(getState());
      return dispatch(fetchPackageProjectsAction(packageLocator, version, hydrateStateQuery));
    };

/**
 * Fetches the package verssion metadata associated with a given package + version.
 *
 * @param {string} packageLocator - Given package locator.
 * @param {string} version - Given package version.
 */
export const fetchPackageVersionMetadataAction = (
  packageLocator: string,
  version: string,
  query?: FetchPackagesQuery,
): ReduxAction<FetchVersionDetailsResponse> => ({
  type: actionTypes.async.GET_PACKAGE_VERSION_METADATA,
  payload: async () => API.get(`${ENDPOINTS.FETCH_PACKAGES}/${nuc(packageLocator)}/versions/${nuc(version)}`, true, query, {
    encodeQuery: true,
  }),
});

/**
 * Fetches the package verssion metadata associated with a given package + version.
 *
 * @param {string} packageLocator - Given package locator.
 * @param {string} version - Given package version.
 */
export const fetchPackageVersionMetadata = (
  packageLocator: string,
  version: string,
): ThunkAction<void, ReduxStore, never, AnyAction> => (dispatch, getState) => {
  const hydrateStateQuery = getHydratePackagesStateQuery(getState());
  return dispatch(fetchPackageVersionMetadataAction(packageLocator, version, hydrateStateQuery));
};

export const resetPackageVersionDetails = () => ({
  type: actionTypes.sync.RESET_PACKAGE_VERSION_DETAILS,
});

export const resetPackageDetails = () => ({
  type: actionTypes.sync.RESET_PACKAGE_DETAILS,
});

/**
 * Fetches the list of package managers so that we can display them
 * first in the package manager dropdown.
 */
export const fetchPackageManagers = () => ({
  type: actionTypes.async.GET_PACKAGE_MANAGERS,
  payload: async () => API.get(`${ENDPOINTS.FETCH_PACKAGES}/package-managers`, true),
});

/**
 * Fetches the list of package locators to be used by the Package Locator filter.
 * Currently configured to fetch only the first 10 locators to better serve the UI - this can be configured as needed
 */
export const fetchPackageLocators = (packageLocator?: string) => ({
  type: actionTypes.async.GET_PACKAGE_LOCATORS,
  payload: async () =>
    API.get(
      `${ENDPOINTS.FETCH_PACKAGES}/package-locators?${packageLocator ? `packageLocator=${packageLocator}&` : ''}count=10&page=1`,
      true,
    ),
});

/**
 * Fetch the package summary (package count and last cache date) to render the CTA.
 */
export const fetchPackageSummary = () => ({
  type: actionTypes.async.GET_PACKAGE_SUMMARY,
  payload: async () => API.get(`${ENDPOINTS.FETCH_PACKAGES}/package-summary`, true),
});

/**
 * Fetches the CWE list
 */
export const fetchCwes = () => ({
  type: actionTypes.async.GET_CWES,
  payload: async () => API.get(`/api/weaknesses`, true),
});

/**
 * After a user selects a vuln in the Vulnerabilities table, we
 * need to fetch the vuln data + the remediation info for that vuln
 * so that we can display it in the Drawer.
 *
 * These are two separate endpoints, but instead of having separate actions
 * and storing them in different parts of the store, we just fetch
 * both here and combine them into a single `selectedVuln` here.
 * This is why this doesn't use the typical redux-promise "async payload" pattern.
 */
export const fetchSelectedVuln =
  (vulnId: string, revisionId: string): ThunkAction<void, ReduxStore, never, AnyAction> =>
    async (dispatch, getState) => {
      const selectedVuln = getSelectedVuln(getState());
      //  Bail if selecting the same vuln again.
      if (selectedVuln?.id === vulnId) {
        return;
      }

      dispatch({ type: asyncActionTypes.GET_SELECTED_VULN.PENDING });
      try {
        const [{ data: vulnData }, { data: remediationData }] = await Promise.all([
          API.get(`/api/vulns/${nuc(vulnId)}`, true),
          API.get(`/api/vulns/${nuc(vulnId)}/revisions/${nuc(revisionId)}/remediation-guidance`, true),
        ]);

        dispatch({
          type: asyncActionTypes.GET_SELECTED_VULN.FULFILLED,
          payload: {
            data: {
              ...(vulnData || {}),
              ...(remediationData || {}),
            },
          },
        });
      } catch (error) {
        dispatch({
          type: asyncActionTypes.GET_SELECTED_VULN.REJECTED,
          payload: error,
        });
      }
    };

//  When the user closes the drawer, we want to clear out the selectedVuln (as none are selected at that point)
export const resetSelectedVuln = () => ({
  type: syncActionTypes.RESET_SELECTED_VULN,
});

//  Toggles if a specific version is selected or not
export const toggleVersionSelected = (packageRevision: PackageDetails['packageRevision']) => ({
  type: syncActionTypes.TOGGLE_VERSION_SELECTED,
  packageRevision,
});

//  Toggles if a ALL versions should be selected or not.
export const toggleSelectAllVersions = () => ({
  type: syncActionTypes.TOGGLE_SELECTED_ALL_VERSIONS,
});

/**
 * Blocks a given set of package versions in a given set of quality policies.
 */
export const blockPackageVersions = ({ packageLocator, policyIds, versions }: {
  packageLocator: string,
  policyIds: number[],
  versions: string[] | 'ALL',
}) => ({
  type: actionTypes.async.BLOCK_PACKAGE_VERSIONS,
  payload: async () => API.post(`/api/packages/${nuc(packageLocator)}/rules`, {
    policyIds,
    versions,
  }),
});
